import { useCallback, useMemo } from "react";

import type { PreviewFillParams } from "@/api/dragon-tiles/previewUserContext";
import type {
  TestEmailAdapter,
  TestEmailSendOptions,
} from "@/components/TestEmailBar/TestEmailBar";

import {
  emailHeaderOverridesForTestSend,
  useSendTestEmailFromPayload,
  toTestSendProductGrids,
  type TestEmailPayload,
} from "@/api/dragon-tiles/email";
import { formatTestSendSubject } from "@/utils/formatTestSendSubject";

export type TestEmailScope = {
  siteId: string;
  frameId: string;
  frameVariantId: string;
  customerId: string;
  payload: TestEmailPayload;
};

export type UseTestEmailOptions = {
  siteId: string;
  frameId: string;
  frameVariantId: string;
  /**
   * Optional; when omitted, `basePayload.contact_id` is used as `customerId` on the request.
   */
  customerId?: string;
  /**
   * Optional ENG-1291 context for mutation key scoping and sent as `preview_fill_params` on the request.
   */
  previewFillParams?: PreviewFillParams;
  basePayload: Omit<TestEmailPayload, "to">;
  resolveBody?: () => Promise<string>;
  /** Late plaintext lookup (e.g. after a full-frame refetch); wins over resolveBody. */
  resolvePlaintext?: () => Promise<string | undefined>;
  requireSubjectOrPreheader?: boolean;
  /**
   * Pre-fills the subject field in the test send UI.
   * Used when there is no saved subject on the tile so the user can send without typing anything.
   */
  defaultSubject?: string;
  onValidationError?: (message: string) => void;
  storageKeyPrefix?: string;
  buttonText?: string;
};

export function useTestEmail({
  siteId,
  frameId,
  frameVariantId,
  customerId: customerIdProp,
  previewFillParams,
  basePayload,
  resolveBody,
  resolvePlaintext,
  requireSubjectOrPreheader = false,
  defaultSubject,
  onValidationError,
  storageKeyPrefix = "email_autocomplete_options_",
  buttonText = "Test It",
}: UseTestEmailOptions): {
  adapter: TestEmailAdapter<TestEmailScope>;
  scope: TestEmailScope;
  storageKey: string;
  readStoredEmails: () => string[];
} {
  const customerId = customerIdProp ?? basePayload.contact_id ?? "";

  const sendTestEmailMutation = useSendTestEmailFromPayload(
    siteId,
    frameVariantId,
    previewFillParams,
  );
  const storageKey = `${storageKeyPrefix}${siteId}`;

  const readStoredEmails = useCallback((): string[] => {
    try {
      if (typeof window === "undefined") return [];
      const raw = localStorage.getItem(storageKey);
      return raw
        ? (JSON.parse(raw) as { name: string }[]).map((e) => e.name)
        : [];
    } catch (e) {
      console.error("Failed to read emails from localStorage:", e);
      return [];
    }
  }, [storageKey]);

  const scope: TestEmailScope = useMemo(
    () => ({
      siteId,
      frameId,
      frameVariantId,
      customerId,
      payload: { to: readStoredEmails(), ...basePayload },
    }),
    [
      siteId,
      frameId,
      frameVariantId,
      customerId,
      basePayload,
      readStoredEmails,
    ],
  );

  const adapter: TestEmailAdapter<TestEmailScope> = {
    storageKey: () => storageKey,
    useHandler: ({ customerId: cid, payload }: TestEmailScope) => ({
      onSend: async (
        emails: string[],
        options?: TestEmailSendOptions,
        body?: string,
      ) => {
        const productGrids = toTestSendProductGrids(payload.product_grids);
        // True-plaintext variants send the raw text body; backend does the
        // text/plain send (ENG-1415). No HTML body accompanies it. Presence is
        // nullish — an empty saved body still takes the plaintext path.
        const plaintext =
          payload.plaintext ??
          (resolvePlaintext ? await resolvePlaintext() : undefined);
        const savedBody = payload.body?.trim() ? payload.body : undefined;
        const resolvedBody =
          plaintext != null
            ? ""
            : (body ?? savedBody ?? (resolveBody ? await resolveBody() : ""));
        const baseSubject = options?.subject ?? payload.subject ?? "";
        sendTestEmailMutation.mutate({
          medium: payload.medium ?? "email",
          contentText: resolvedBody,
          ...(plaintext != null ? { contentPlaintext: plaintext } : {}),
          config: {
            previewText: payload.preheader ?? "",
            subjectLine: formatTestSendSubject(baseSubject),
            offers: payload.offers ?? {},
            ...(productGrids ? { productGrids } : {}),
          },
          email: emails,
          phone: payload.phone ?? [],
          customerId: cid,
          ...emailHeaderOverridesForTestSend(payload),
          ...(previewFillParams
            ? {
                preview_fill_params: {
                  ...previewFillParams,
                },
              }
            : {}),
        });
      },
      isSending: sendTestEmailMutation.isPending,
    }),

    buttonText,
    defaultSubject: basePayload.subject || defaultSubject,

    beforeSend: async (options?: TestEmailSendOptions) => {
      if (!requireSubjectOrPreheader) return Promise.resolve(true);
      // When options is provided, user input takes precedence over basePayload
      // Only fall back to basePayload.subject when options is undefined
      const hasSubject = options
        ? Boolean(options.subject)
        : Boolean(basePayload.subject);
      const ok = hasSubject || Boolean(basePayload.preheader);

      if (!ok) onValidationError?.("Please enter a subject line or preheader");
      return Promise.resolve(ok);
    },
  };

  return { adapter, scope, storageKey, readStoredEmails };
}
